import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { aiInstructionsAPI } from '@/utils/api';
import useAppStore from '@/store/appStore';

/**
 * Fetch all AI instructions with optional filters
 */
export function useAiInstructions(scope = null, enabled = true) {
  return useQuery({
    queryKey: ['ai-instructions', scope, enabled],
    queryFn: async () => {
      const params = {};
      if (scope) params.scope = scope;
      if (enabled !== null) params.enabled = enabled;
      const response = await aiInstructionsAPI.getAll(params);
      return response;
    },
    select: (response) => {
      if (!response || !response.success) return [];
      const data = response.data;
      return Array.isArray(data) ? data : [];
    }
  });
}

/**
 * Fetch a single AI instruction by ID
 */
export function useAiInstruction(id) {
  return useQuery({
    queryKey: ['ai-instruction', id],
    queryFn: () => aiInstructionsAPI.getById(id),
    select: (response) => response.data,
    enabled: !!id
  });
}

/**
 * Create a new AI instruction
 */
export function useCreateAiInstruction() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: aiInstructionsAPI.create,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['ai-instructions'] });
      addNotification({
        type: 'success',
        message: `Created instruction: ${response.data.display_name}`
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        message: `Failed to create instruction: ${error.message}`
      });
    }
  });
}

/**
 * Update an existing AI instruction
 */
export function useUpdateAiInstruction() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: ({ id, updates }) => aiInstructionsAPI.update(id, updates),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['ai-instructions'] });
      queryClient.invalidateQueries({ queryKey: ['ai-instruction', response.data.id] });
      addNotification({
        type: 'success',
        message: `Updated instruction: ${response.data.display_name}`
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        message: `Failed to update instruction: ${error.message}`
      });
    }
  });
}

/**
 * Delete an AI instruction
 */
export function useDeleteAiInstruction() {
  const queryClient = useQueryClient();
  const addNotification = useAppStore((state) => state.addNotification);

  return useMutation({
    mutationFn: aiInstructionsAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-instructions'] });
      addNotification({
        type: 'success',
        message: 'Instruction deleted'
      });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        message: `Failed to delete instruction: ${error.message}`
      });
    }
  });
}
